// src/vetPanel/VetLoginPage.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithPopup, signInWithRedirect } from "firebase/auth";
import { solicitarNuevoEnlaceVet } from "../services/api";
import { initFirebase, googleProvider } from '../firebaseConfig';
import { API_URL } from '../services/api/config';

// Detectar si es móvil
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

// Light theme color palette (LemmonTags brand)
const colors = {
  bg: '#fafafa',
  surface: '#ffffff',
  surfaceHover: '#f5f5f5',
  border: '#e5e7eb',
  borderFocus: '#84cc16',
  accent: '#84cc16',
  accentDark: '#65a30d',
  accentLight: '#ecfccb',
  textPrimary: '#1f2937',
  textSecondary: '#6b7280',
  textMuted: '#9ca3af',
  error: '#dc2626',
  errorBg: '#fee2e2',
  success: '#16a34a',
  successBg: '#dcfce7',
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: colors.bg,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "2rem 1rem",
  },
  card: {
    width: "100%",
    maxWidth: "400px",
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: "12px",
    padding: "2rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  },
  logo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    marginBottom: "2rem",
  },
  logoIcon: {
    width: "32px",
    height: "32px",
    backgroundColor: colors.accent,
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  logoText: {
    fontSize: "1.25rem",
    fontWeight: 600,
    color: colors.textPrimary,
    letterSpacing: "-0.025em",
  },
  badge: {
    fontSize: "0.625rem",
    fontWeight: 500,
    color: colors.accentDark,
    backgroundColor: colors.accentLight,
    padding: "0.125rem 0.5rem",
    borderRadius: "9999px",
    marginLeft: "0.5rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  title: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: colors.textPrimary,
    textAlign: "center",
    marginBottom: "0.5rem",
    letterSpacing: "-0.025em",
  },
  subtitle: {
    fontSize: "0.875rem",
    color: colors.textSecondary,
    textAlign: "center",
    marginBottom: "1.5rem",
    lineHeight: 1.5,
  },
  label: {
    display: "block",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: colors.textPrimary,
    marginBottom: "0.5rem",
  },
  input: {
    width: "100%",
    padding: "0.75rem 1rem",
    fontSize: "0.875rem",
    color: colors.textPrimary,
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
    boxSizing: "border-box",
  },
  inputFocus: {
    borderColor: colors.accent,
    boxShadow: `0 0 0 3px rgba(132, 204, 22, 0.15)`,
  },
  button: {
    width: "100%",
    padding: "0.75rem 1rem",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "#ffffff",
    backgroundColor: colors.accent,
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background-color 0.2s, transform 0.1s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  alert: {
    padding: "0.75rem 1rem",
    borderRadius: "8px",
    fontSize: "0.875rem",
    marginBottom: "1rem",
    display: "flex",
    alignItems: "flex-start",
    gap: "0.5rem",
  },
  alertError: {
    backgroundColor: colors.errorBg,
    border: "none",
    color: colors.error,
  },
  alertSuccess: {
    backgroundColor: colors.successBg,
    border: "none",
    color: colors.success,
  },
  footer: {
    padding: "1.5rem",
    textAlign: "center",
    borderTop: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
  },
  footerText: {
    fontSize: "0.75rem",
    color: colors.textMuted,
    marginBottom: "0.5rem",
  },
  footerLinks: {
    display: "flex",
    justifyContent: "center",
    gap: "1.5rem",
    flexWrap: "wrap",
  },
  footerLink: {
    color: colors.textSecondary,
    textDecoration: "none",
    fontSize: "0.75rem",
    transition: "color 0.2s",
  },
  spinner: {
    width: "16px",
    height: "16px",
    border: "2px solid transparent",
    borderTopColor: "#ffffff",
    borderRadius: "50%",
    animation: "spin 0.6s linear infinite",
  },
  divider: {
    display: "flex",
    alignItems: "center",
    margin: "1.5rem 0",
    color: colors.textMuted,
    fontSize: "0.75rem",
  },
  dividerLine: {
    flex: 1,
    height: "1px",
    backgroundColor: colors.border,
  },
  dividerText: {
    padding: "0 1rem",
  },
  googleButton: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.75rem",
    padding: "0.75rem 1rem",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: '#3c4043',
    backgroundColor: '#ffffff',
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background-color 0.2s, box-shadow 0.2s",
  },
  googleButtonHover: {
    backgroundColor: '#f8f9fa',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  googleButtonDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
};

// Add keyframes for spinner animation
const spinnerKeyframes = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// Google Icon SVG Component
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export default function VetLoginPage() {
  const [correo, setCorreo] = useState("");
  const [error, setError] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [googleButtonHovered, setGoogleButtonHovered] = useState(false);
  const navigate = useNavigate();

  // Manejar resultado del redirect de Google (para móvil)
  // IMPORTANTE: Usamos onAuthStateChanged porque getRedirectResult falla en Safari iOS
  useEffect(() => {
    const isPending = sessionStorage.getItem('googleVetSignInPending');

    // Si no hay redirect pendiente, no hacer nada
    if (!isPending) {
      return;
    }

    setGoogleLoading(true);

    let unsubscribe = () => {};

    const setupAuthListener = async () => {
      try {
        const { auth } = await initFirebase();

        // Si ya hay usuario autenticado, procesarlo inmediatamente
        if (auth.currentUser) {
          const user = auth.currentUser;
          sessionStorage.removeItem('googleVetSignInPending');

          try {
            const idToken = await user.getIdToken();

            const response = await fetch(`${API_URL}/api/auth/google-login-vet`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ idToken }),
            });

            const data = await response.json();

            if (!response.ok) {
              throw new Error(data.error || 'Error al autenticar con Google');
            }

            localStorage.setItem('vetToken', data.token);
            localStorage.setItem('loginMethod', 'google');

            window.location.replace(`/vetpanel?doc=${data.vetId}&token=${data.token}`);
            return; // Exit early, no need for listener
          } catch (err) {
            setError(err.message || "Error al iniciar sesión con Google");
            setGoogleLoading(false);
            return;
          }
        }

        // Si no hay currentUser, configurar listener para esperar
        // onAuthStateChanged es más confiable que getRedirectResult en Safari iOS
        unsubscribe = auth.onAuthStateChanged(async (user) => {
          if (user) {
            // Usuario autenticado después del redirect
            sessionStorage.removeItem('googleVetSignInPending');

            try {
              const idToken = await user.getIdToken();

              const response = await fetch(`${API_URL}/api/auth/google-login-vet`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ idToken }),
              });

              const data = await response.json();

              if (!response.ok) {
                throw new Error(data.error || 'Error al autenticar con Google');
              }

              localStorage.setItem('vetToken', data.token);
              localStorage.setItem('loginMethod', 'google');

              window.location.replace(`/vetpanel?doc=${data.vetId}&token=${data.token}`);
            } catch (err) {
              setError(err.message || "Error al iniciar sesión con Google");
              setGoogleLoading(false);
            }
          }
        });

        // Timeout de seguridad - si después de 8 segundos no hay usuario, mostrar error
        setTimeout(() => {
          const stillPending = sessionStorage.getItem('googleVetSignInPending');
          if (stillPending) {
            sessionStorage.removeItem('googleVetSignInPending');
            setGoogleLoading(false);
            setError('No se pudo completar el inicio de sesión. Por favor intenta de nuevo.');
          }
        }, 8000);

      } catch (err) {
        sessionStorage.removeItem('googleVetSignInPending');
        setError(err.message || "Error al iniciar sesión con Google");
        setGoogleLoading(false);
      }
    };

    setupAuthListener();

    // Cleanup
    return () => {
      unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setMensaje("");

    if (!correo.trim()) {
      setError("Por favor, escribe tu correo.");
      return;
    }

    setLoading(true);

    try {
      const resp = await solicitarNuevoEnlaceVet(correo);

      if (resp.error) {
        setError(resp.error);
      } else if (resp.success) {
        setMensaje(resp.message || "Hemos enviado un nuevo enlace a tu correo.");
      } else {
        setError("Respuesta desconocida del servidor.");
      }
    } catch (err) {
      setError("Error de conexión. Por favor intenta más tarde.");
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    setError("");
    setMensaje("");
    setGoogleLoading(true);

    try {
      const { auth } = await initFirebase();

      // En móvil usar redirect (evita bloqueo de popup), en desktop usar popup
      if (isMobile) {
        sessionStorage.setItem('googleVetSignInPending', 'true');
        await signInWithRedirect(auth, googleProvider);
        // El código no continúa aquí - el navegador redirige a Google
        return;
      }

      // Desktop: usar popup (mejor UX)
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Obtener el ID token para verificar en el backend
      const idToken = await user.getIdToken();

      // Llamar al backend para verificar veterinario y obtener JWT
      const response = await fetch(`${API_URL}/api/auth/google-login-vet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al autenticar con Google');
      }

      // Guardar token y método de login
      localStorage.setItem('vetToken', data.token);
      localStorage.setItem('loginMethod', 'google');

      // Redirigir al panel de veterinario (window.location.replace para limpiar historial OAuth)
      window.location.replace(`/vetpanel?doc=${data.vetId}&token=${data.token}`);

    } catch (err) {
      console.error("Error en Google Sign-In:", err);

      // Manejar errores específicos de Google
      if (err.code === 'auth/popup-closed-by-user') {
        // Usuario cerró el popup, no mostrar error
        return;
      } else if (err.code === 'auth/cancelled-popup-request') {
        // Múltiples popups, ignorar
        return;
      } else if (err.code === 'auth/popup-blocked') {
        setError("El navegador bloqueó la ventana emergente. Por favor, permite las ventanas emergentes para este sitio.");
      } else {
        setError(err.message || "Error al iniciar sesión con Google. Por favor intenta de nuevo.");
      }
    } finally {
      setGoogleLoading(false);
    }
  }

  return (
    <div style={styles.container}>
      <style>{spinnerKeyframes}</style>

      <div style={styles.main}>
        <div style={styles.card}>
          {/* Logo */}
          <div style={styles.logo}>
            <div style={styles.logoIcon}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <span style={styles.logoText}>LemmonTags</span>
            <span style={styles.badge}>Veterinarios</span>
          </div>

          {/* Title */}
          <h1 style={styles.title}>Ingreso para Veterinarios</h1>
          <p style={styles.subtitle}>
            Bienvenido a la plataforma para gestionar pacientes veterinarios.
            Ingresa tu correo para solicitar un enlace de acceso.
          </p>

          {/* Error message */}
          {error && (
            <div style={{...styles.alert, ...styles.alertError}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M15 9l-6 6M9 9l6 6"/>
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Success message */}
          {mensaje && (
            <div style={{...styles.alert, ...styles.alertSuccess}}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <span>{mensaje}</span>
            </div>
          )}

          {/* Google Sign-In Button */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={googleLoading || loading || mensaje}
            onMouseEnter={() => setGoogleButtonHovered(true)}
            onMouseLeave={() => setGoogleButtonHovered(false)}
            style={{
              ...styles.googleButton,
              ...(googleButtonHovered && !googleLoading && !loading && !mensaje ? styles.googleButtonHover : {}),
              ...((googleLoading || loading || mensaje) ? styles.googleButtonDisabled : {}),
            }}
          >
            {googleLoading ? (
              <>
                <div style={{...styles.spinner, borderTopColor: '#3c4043'}} />
                Conectando...
              </>
            ) : (
              <>
                <GoogleIcon />
                Continuar con Google
              </>
            )}
          </button>

          {/* Divider */}
          <div style={styles.divider}>
            <div style={styles.dividerLine} />
            <span style={styles.dividerText}>o ingresa tu correo</span>
            <div style={styles.dividerLine} />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "1rem" }}>
              <label style={styles.label}>
                Correo electrónico
              </label>
              <input
                type="email"
                placeholder="doctor@clinicavet.com"
                value={correo}
                onChange={(e) => setCorreo(e.target.value)}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                disabled={loading || googleLoading || mensaje}
                style={{
                  ...styles.input,
                  ...(inputFocused ? styles.inputFocus : {}),
                  opacity: (loading || googleLoading || mensaje) ? 0.5 : 1,
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading || googleLoading || mensaje}
              style={{
                ...styles.button,
                ...((loading || googleLoading || mensaje) ? styles.buttonDisabled : {}),
              }}
            >
              {loading ? (
                <>
                  <div style={styles.spinner} />
                  Enviando...
                </>
              ) : (
                "Solicitar nuevo enlace"
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Footer */}
      <footer style={styles.footer}>
        <p style={styles.footerText}>© 2025 LemmonTags Veterinarios</p>
        <div style={styles.footerLinks}>
          <a href="/terminos" style={styles.footerLink}>Términos y Condiciones</a>
          <a href="/privacidad" style={styles.footerLink}>Política de Privacidad</a>
          <a href="mailto:soporte@lemmontags.com" style={styles.footerLink}>Contacto</a>
        </div>
      </footer>
    </div>
  );
}
